import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { EASE } from "./Reveal";
import { scrollToSection } from "./Header";

const LINES = ["Jūs redzat, kuri baneri/video zaudē.", "Bet vai zināt, KĀPĒC?"];

const DOTS = [
  { x: 50, y: 12, label: "Brand Consistency", note: "Vai zīmols ir uzreiz atpazīstams un paliek pircēja atmiņā?" },
  { x: 50, y: 25, label: "Hook Strength", note: "Vai galvenais vēstījums ir pietiekami spēcīgs, lai apturētu potenciālā pircēja uzmanību?" },
  { x: 50, y: 38.5, label: "Supporting Proof", note: "Vai argumenti un pierādījumi ir pietiekami spēcīgi, lai pircējs noticētu piedāvājumam?" },
  { x: 16, y: 47.6, label: "Message Clarity", note: "Vai skatītājs ātri saprot, ko Jūs piedāvājat un kāpēc tas viņam ir aktuāli?" },
  { x: 50, y: 62, label: "Creative Impact", note: "Vai produkta vizuālis piesaista uzmanību un palīdz pārdot, nevis tikai aizpilda vietu?" },
  { x: 50, y: 90.5, label: "CTA Effectiveness", note: "Vai CTA ir pamanāms un pietiekami spēcīgi virza potenciālo klientu uz nākamo soli?" },
];

const Hotspot = ({ dot, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="absolute" style={{ left: `${dot.x}%`, top: `${dot.y}%` }}>
      <button
        data-testid={`hero-banner-dot-${index}`}
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="relative flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#ED1C27] text-white font-bold shadow-[0_0_0_6px_rgba(255,87,51,0.25)] hover:scale-110 transition-transform duration-200"
        aria-label={dot.label}
      >
        <Plus className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
        <span className="absolute inset-0 rounded-full bg-[#ED1C27] animate-ping opacity-30" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className={`absolute z-20 w-60 rounded-sm bg-white border border-black/10 shadow-xl p-4 pointer-events-none ${
              dot.x > 55 ? "right-5" : "left-5"
            } ${dot.y > 55 ? "bottom-5" : "top-5"}`}
            data-testid={`hero-banner-dot-${index}-tooltip`}
          >
            <p className="font-display font-bold text-sm tracking-tight text-[#003E75]">{dot.label}</p>
            <p className="mt-1.5 text-xs text-[#003E75]/60 leading-relaxed">{dot.note}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-white">
      <span
        aria-hidden="true"
        className="font-display font-black uppercase text-[24vw] leading-none text-outline-dark opacity-[0.06] absolute -right-10 top-24 select-none pointer-events-none"
      >
        Audits
      </span>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-36 pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            data-testid="hero-kicker"
            className="text-xs font-semibold uppercase tracking-[0.35em] text-[#ED1C27] mb-8"
          >
            iMarketings.lv - bezmaksas kreatīvo audits
          </motion.p>

          <h1 className="font-display font-black uppercase tracking-tighter leading-[1.02] text-[#003E75] text-[clamp(1.9rem,3.2vw,3rem)]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.25 + i * 0.13, ease: EASE }}
                  className="block"
                  data-testid={`hero-line-${i}`}
                >
                  {i === 0 ? (
                    <>
                      Jūs redzat, kuri <span className="text-[#ED1C27]">baneri/video</span> zaudē.
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
          >
            <p className="mt-8 font-semibold tracking-tight text-xl md:text-2xl text-[#003E75]/85 max-w-xl" data-testid="hero-subheading">
              Noskaidrojiet, kas jāizmaina Jūsu reklāmas materiālos, lai uzlabotu rezultātu?
            </p>
            <p className="mt-5 text-base text-[#003E75]/55 max-w-xl leading-relaxed">
              Netestējiet kļūdas. Vispirms identificējiet tās (lai nezaudētu naudu)
            </p>
            <div className="mt-9 inline-flex flex-col items-center">
              <button
                data-testid="hero-cta-button"
                onClick={() => scrollToSection("#pieteikties")}
                className="group inline-flex items-center gap-3 bg-[#ED1C27] text-white font-display font-bold uppercase tracking-wide px-9 py-4 rounded-full hover:scale-105 transition-transform duration-300"
              >
                Pieteikties bezmaksas auditam
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
              </button>
              <p className="mt-4 text-sm text-[#003E75]/50 text-center" data-testid="hero-experience">
                50,000 kreatīvu izstrādes pieredze
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          style={{ y: cardY }}
          initial={{ opacity: 0, y: 60, rotate: 2.5 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.85, ease: EASE }}
          className="hidden lg:block"
          data-testid="hero-banner-visual"
        >
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative rounded-sm overflow-hidden border border-black/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)] max-w-md ml-auto"
          >
            <img src="/assets/example-banner.jpg" alt="Banera audita piemērs" className="block w-full" />
            {DOTS.map((d, i) => (
              <Hotspot key={i} dot={d} index={i} />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
